/**
 * Date Utility Functions
 * Provides ISO week calculations and standardized date formatting
 *
 * ISO Standards:
 * - ISO weeks run Monday-Sunday with week 1 containing January 4th
 * - ISO months: YYYY-MM format (e.g., "2024-03")
 * - ISO quarters: YYYY-QN format (e.g., "2024-Q1")
 *
 * This module provides both ISO standard formats and human-readable labels
 */

/**
 * Get ISO week number from a date
 * ISO 8601 week: Monday is day 1, Sunday is day 7
 * Week 1 is the week containing January 4th
 */
export function getISOWeekNumber(date: Date): number {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  // Set to nearest Thursday (current date + 4 - current day number, making Monday = 1)
  d.setDate(d.getDate() + 3 - ((d.getDay() + 6) % 7));
  // January 4th is always in week 1
  const week1 = new Date(d.getFullYear(), 0, 4);
  // Calculate week number
  return 1 + Math.round(((d.getTime() - week1.getTime()) / 86400000 - 3 + ((week1.getDay() + 6) % 7)) / 7);
}

/**
 * Get the Monday date of an ISO week
 * Returns the first day (Monday) of the specified ISO week
 */
export function getDateFromISOWeek(week: number, year: number): Date {
  const jan4 = new Date(year, 0, 4);
  const dayOfWeek = jan4.getDay();
  // Find the Monday of week 1
  const mondayOfWeek1 = new Date(jan4);
  mondayOfWeek1.setDate(jan4.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));

  // Calculate the Monday of the target week
  const targetMonday = new Date(mondayOfWeek1);
  targetMonday.setDate(mondayOfWeek1.getDate() + (week - 1) * 7);

  return targetMonday;
}

/**
 * Get the Monday-Sunday range for an ISO week
 * Returns an object with start (Monday) and end (Sunday) dates
 */
export function getISOWeekDateRange(week: number, year: number): { start: Date; end: Date } {
  const monday = getDateFromISOWeek(week, year);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);

  return { start: monday, end: sunday };
}

/**
 * Format ISO week as a label like "W38 (Sep 15-21)"
 * Shows the week number and date range
 */
export function formatISOWeekLabel(week: number, year: number): string {
  const { start, end } = getISOWeekDateRange(week, year);

  const startMonth = start.toLocaleDateString('en-US', { month: 'short' });
  const startDay = start.getDate();
  const endDay = end.getDate();

  // If start and end are in the same month
  if (start.getMonth() === end.getMonth()) {
    return `W${week} (${startMonth} ${startDay}-${endDay})`;
  }

  // If they span different months
  const endMonth = end.toLocaleDateString('en-US', { month: 'short' });
  return `W${week} (${startMonth} ${startDay}-${endMonth} ${endDay})`;
}

/**
 * Get the month that contains the majority of an ISO week
 * Returns ISO format: YYYY-MM
 * Used for aggregating weekly data into months
 */
export function getMonthFromISOWeek(week: number, year: number): string {
  const { start } = getISOWeekDateRange(week, year);

  // Use the Thursday of the week (middle of the week) to determine the month
  const thursday = new Date(start);
  thursday.setDate(start.getDate() + 3);

  const month = thursday.getMonth() + 1; // getMonth() returns 0-11, we need 1-12
  const isoMonth = month.toString().padStart(2, '0');

  return `${thursday.getFullYear()}-${isoMonth}`;
}

/**
 * Get human-readable month label from ISO week (for display purposes)
 * Returns format like "Mar 2024"
 */
export function getMonthLabelFromISOWeek(week: number, year: number): string {
  const { start } = getISOWeekDateRange(week, year);
  const thursday = new Date(start);
  thursday.setDate(start.getDate() + 3);

  return `${thursday.toLocaleDateString('en-US', { month: 'short' })} ${thursday.getFullYear()}`;
}

/**
 * Get the quarter that contains an ISO week
 * Returns ISO format: YYYY-QN
 * Based on the Thursday of the week (middle day)
 */
export function getQuarterFromISOWeek(week: number, year: number): string {
  const { start } = getISOWeekDateRange(week, year);

  // Use the Thursday of the week to determine the quarter
  const thursday = new Date(start);
  thursday.setDate(start.getDate() + 3);

  const month = thursday.getMonth();
  const quarter = Math.floor(month / 3) + 1;

  return `${thursday.getFullYear()}-Q${quarter}`;
}

/**
 * Get human-readable quarter label from ISO week (for display purposes)
 * Returns format like "Q1 2024"
 */
export function getQuarterLabelFromISOWeek(week: number, year: number): string {
  const { start } = getISOWeekDateRange(week, year);
  const thursday = new Date(start);
  thursday.setDate(start.getDate() + 3);

  const month = thursday.getMonth();
  const quarter = Math.floor(month / 3) + 1;

  return `Q${quarter} ${thursday.getFullYear()}`;
}

/**
 * Parse a week label like "W38 (Sep 15)" to extract week and year
 * Handles various formats from the backend
 */
export function parseWeekLabel(label: string): { week: number; year: number } {
  // Extract week number from formats like "W27", "W27 (Jul 01)", etc.
  const weekMatch = label.match(/W(\d+)/);
  const week = weekMatch ? parseInt(weekMatch[1], 10) : 1;

  // Try to extract year from label (e.g., "2024" or "'24")
  const yearMatch = label.match(/(20\d{2})|'(\d{2})/);
  let year = new Date().getFullYear();

  if (yearMatch) {
    if (yearMatch[1]) {
      year = parseInt(yearMatch[1], 10);
    } else if (yearMatch[2]) {
      year = 2000 + parseInt(yearMatch[2], 10);
    }
  }

  return { week, year };
}

/**
 * Parse ISO month string (YYYY-MM) to get display label
 * @param isoMonth - ISO month string like "2024-03"
 * @returns Human readable like "Mar 2024"
 */
export function parseISOMonth(isoMonth: string): string {
  const [year, month] = isoMonth.split('-');
  const date = new Date(parseInt(year), parseInt(month) - 1, 1);
  return `${date.toLocaleDateString('en-US', { month: 'short' })} ${year}`;
}

/**
 * Parse ISO quarter string (YYYY-QN) to get display label
 * @param isoQuarter - ISO quarter string like "2024-Q1"
 * @returns Human readable like "Q1 2024"
 */
export function parseISOQuarter(isoQuarter: string): string {
  const [year, quarter] = isoQuarter.split('-');
  return `${quarter} ${year}`;
}

/**
 * Validate that a date falls within a specific ISO week
 * Useful for debugging and testing
 */
export function isDateInISOWeek(date: Date, week: number, year: number): boolean {
  const { start, end } = getISOWeekDateRange(week, year);
  const checkDate = new Date(date);
  checkDate.setHours(0, 0, 0, 0);
  start.setHours(0, 0, 0, 0);
  end.setHours(23, 59, 59, 999);

  return checkDate >= start && checkDate <= end;
}